import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button } from 'design-system/ui/atoms/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from 'design-system/ui/atoms/dropdown/dropdown-menu'
import { Input } from 'design-system/ui/atoms/input'
import { Pill } from 'design-system/ui/atoms/pill'
import Fuse from 'fuse.js'
import debounce from 'lodash.debounce'
import {
  CaseSensitiveIcon,
  ChevronRightIcon,
  FilterIcon,
  PlusIcon,
  XIcon,
} from 'lucide-react'

export interface Filter {
  id: string
  label: string
  field: string
  operator: string
  value?: string | undefined
  variant: 'default' | 'primary' | 'secondary' | 'destructive' | 'success'
}

interface FilterField {
  name: string
  description?: string
  operators: {
    operator: string
    pattern: string
  }[]
}

interface FilterBarProps {
  fields: FilterField[]
  operators?: string[]
  onFilterChange?: (filters: Filter[]) => void
  defaultFilters?: Filter[]
  className?: string
  filters: Filter[]
  setFilters: (filters: Filter[]) => void
  setCaseSensitive: (caseSensitive: boolean) => void
  caseSensitive: boolean
}

interface SearchInputProps {
  value: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onSubmit: (
    label: string,
    selectedField: string,
    operator: string,
    value?: string,
  ) => void
  fields: FilterField[]
  operators: string[]
  forwardRef: React.RefObject<HTMLInputElement>
}

interface FieldListProps {
  filteredFields: string[]
  onFieldClick: (field: string) => void
  onFieldHover: (field: string | null) => void
  selectedField: string | null
  fields: FilterField[]
}

interface FieldDetailsProps {
  showOperators?: boolean
  hoveredField?: string | null
  selectedField?: string | null
  onOperatorSelect?: (field: string, operator: string) => void
  operators: string[]
  fields: FilterField[]
}

interface FilterPillsProps {
  filters: Filter[]
  onRemove: (id: string) => void
  onEdit: (id: string) => void
}

interface FilterDropdownContentProps {
  searchValue: string
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  filteredFields: string[]
  onFieldSelect: (
    label: string,
    field: string,
    operator: string,
    value?: string,
  ) => void
  fields: FilterField[]
  operators: string[]
  onClose: () => void
  isOpen: boolean
}

// Search Input Component
const SearchInput = ({
  value,
  onChange,
  onSubmit,
  fields,
  forwardRef,
}: SearchInputProps) => {
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)

  // Create searchable items array with field+operator combinations
  const searchItems = fields.flatMap((field) =>
    field.operators.map((op) => ({
      field: field.name,
      operator: op.operator,
      pattern: op.pattern,
      display: `${field.name} ${op.operator}`,
      searchStr: `${field.name} ${op.operator}`,
    })),
  )

  // Configure Fuse for fuzzy search
  const fuse = new Fuse(searchItems, {
    keys: ['searchStr'],
    threshold: 0.3,
    distance: 100,
  })

  const handleSuggestionClick = (field: string, operator: string) => {
    // Find the field and operator pattern
    const fieldConfig = fields.find((f) => f.name === field)
    const operatorConfig = fieldConfig?.operators.find(
      (op) => op.operator === operator,
    )
    const pattern = operatorConfig?.pattern

    // Check if pattern requires quotes
    const needsQuotes = pattern?.includes('"')
    const newValue = `${field} ${operator}${needsQuotes ? ' ""' : ''}`

    onChange({
      target: { value: newValue },
    } as React.ChangeEvent<HTMLInputElement>)
    setShowSuggestions(false)
    setSelectedIndex(-1)

    if (!needsQuotes) {
      onSubmit(newValue, field, operator)
      onChange({ target: { value: '' } } as React.ChangeEvent<HTMLInputElement>)
    }

    forwardRef.current!.focus()

    // Set cursor position inside quotes if needed
    setTimeout(() => {
      if (forwardRef.current && needsQuotes) {
        const inputElement = forwardRef.current as HTMLInputElement
        inputElement.setSelectionRange(newValue.length - 1, newValue.length - 1)
      }
    }, 0)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
        const suggestion = suggestions[selectedIndex]
        if (suggestion) {
          handleSuggestionClick(suggestion.field, suggestion.operator)
        }
      } else if (value) {
        // Parse value to check against regex pattern
        const [fieldName, operator, ...valueParts] = value.split(' ')
        const filterValue = valueParts.join(' ').replace(/^"|"$/g, '')

        const field = fields.find((f) => f.name === fieldName)
        const operatorConfig = field?.operators.find(
          (op) => op.operator === operator,
        )

        if (field && operatorConfig) {
          const pattern = new RegExp(operatorConfig.pattern)
          if (pattern.test(value)) {
            onSubmit(value, fieldName!, operator!, filterValue)
            onChange({
              target: { value: '' },
            } as React.ChangeEvent<HTMLInputElement>)
            setShowSuggestions(false)
            setSelectedIndex(-1)
          }
        }
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex((prev) =>
        prev < suggestions.length - 1 ? prev + 1 : prev,
      )
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex((prev) => (prev > -1 ? prev - 1 : -1))
    } else if (e.key === 'Escape') {
      setShowSuggestions(false)
      setSelectedIndex(-1)
    }
  }

  const suggestions = useMemo(() => {
    // Get fuzzy search results
    const suggestions = value
      ? fuse.search(value).map((result) => result.item)
      : searchItems

    // If current value matches any item exactly, include it in suggestions
    const currentItems = searchItems
      .filter((item) => {
        const [fieldName, operator] = value.split(' ')
        const matches = item.field === fieldName && item.operator === operator
        return matches
      })
      .map((a) => {
        // Get all words after field and operator
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const [key, operator, ...rest] = value.split(' ')
        const restValue = rest.join(' ')

        // If rest value exists and isn't already in quotes, wrap in quotes
        const formattedValue =
          rest.length > 0 && !restValue.match(/^".*"$/)
            ? `${a.field} ${a.operator} "${restValue}"`
            : `${a.field} ${a.operator} ${restValue}`
        return {
          ...a,
          display: formattedValue,
        }
      })

    // Combine exact matches with fuzzy search results, removing duplicates
    const combinedSuggestions = [...currentItems, ...suggestions]

    // suggestions = uniqueSuggestions;

    return combinedSuggestions
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, value])

  return (
    <div className="ds-relative">
      <div>
        <Input
          ref={forwardRef}
          placeholder="Search fields..."
          value={value}
          onChange={(e) => {
            onChange(e)
            setShowSuggestions(true)
            setSelectedIndex(-1)
          }}
          size="32"
          onKeyDown={handleKeyDown}
          className="ds-h-8 ds-w-full ds-border-half ds-text-[#000] ds-outline-none"
          role="combobox"
          autoFocus={true}
          aria-expanded={showSuggestions}
          aria-controls="suggestions-list"
          aria-activedescendant={
            selectedIndex >= 0 ? `suggestion-${selectedIndex}` : undefined
          }
        />
      </div>
      {showSuggestions && suggestions.length > 0 && value && (
        <div
          id="suggestions-list"
          role="listbox"
          className="ds-border ds-rounded-md flex flex-col ds-absolute ds-z-[99999] ds-mt-1 ds-w-full ds-gap-[6px] ds-border-[#e5e7eb] ds-bg-[#fff] ds-text-[#000]  ds-shadow-lg"
          style={{ paddingTop: '8px', paddingBottom: '8px' }}
        >
          {suggestions.map(({ field, operator, display }, index) => (
            <div
              id={`suggestion-${index}`}
              key={index}
              role="option"
              aria-selected={index === selectedIndex}
              className={`ds-cursor-pointer ds-px-4 ds-py-2 ds-text-[14px] ${
                index === selectedIndex
                  ? 'ds-bg-[#f3f4f6]'
                  : 'ds-hover:bg-[#f3f4f6]'
              }`}
              onClick={() => handleSuggestionClick(field, operator)}
            >
              {display}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// Field List Component
const FieldList = ({
  filteredFields,
  onFieldClick,
  onFieldHover,
  fields,
}: FieldListProps) => {
  const [focusedIndex, setFocusedIndex] = useState(-1)
  const listRef = useRef<HTMLDivElement>(null)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedHover = useCallback(
    debounce((field: string | null) => {
      onFieldHover(field)
    }, 50),
    [onFieldHover],
  )

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setFocusedIndex((prev) =>
        prev < filteredFields.length - 1 ? prev + 1 : prev,
      )
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setFocusedIndex((prev) => (prev > 0 ? prev - 1 : 0))
    } else if (e.key === 'Enter' && focusedIndex >= 0) {
      e.preventDefault()
      if (filteredFields[focusedIndex]) {
        onFieldClick(filteredFields[focusedIndex] || '')
      }
    } else if (e.key === 'ArrowRight' && focusedIndex >= 0) {
      e.preventDefault()
      if (filteredFields[focusedIndex]) {
        onFieldClick(filteredFields[focusedIndex] || '')
      }
    }
  }

  useEffect(() => {
    const listElement = listRef.current
    if (listElement) {
      listElement.addEventListener('keydown', handleKeyDown)
      return () => {
        listElement.removeEventListener('keydown', handleKeyDown)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusedIndex, filteredFields])

  return (
    <div
      ref={listRef}
      className="ds-border-r ds-w-1/3 ds-border-r-half ds-border-white-300 ds-bg-white-50/80 ds-p-8 ds-text-[#000]"
      style={{ paddingTop: '8px', outline: 'none' }}
      role="listbox"
      aria-label="Field list"
      tabIndex={0}
    >
      <div className="ds-flex ds-h-[calc(100%-56px)] ds-flex-col ds-gap-[6px] ds-overflow-y-auto">
        {filteredFields.map((fieldName, index) => {
          const field = fields.find((f) => f.name === fieldName)
          if (!field) return null

          return (
            <div
              id={`field-${index}`}
              key={fieldName}
              role="option"
              aria-selected={focusedIndex === index}
              tabIndex={focusedIndex === index ? 0 : -1}
              className={`ds-box-border ds-flex ds-h-[28px] ds-cursor-pointer ds-items-center ds-justify-between ds-rounded-[8px] ds-px-8 ds-py-4 hover:ds-border-half hover:ds-border-white-300 hover:ds-bg-white-0 ${
                focusedIndex === index
                  ? 'ds-border-half ds-border-white-300 ds-bg-white-0'
                  : ''
              }`}
              style={{ outline: 'none' }}
              onMouseEnter={() => {
                setFocusedIndex(index)
                debouncedHover(fieldName)
              }}
              onMouseLeave={() => debouncedHover(null)}
              onClick={() => onFieldClick(fieldName)}
            >
              <span className="ds-text-13">{fieldName}</span>
              <ChevronRightIcon color="#6b7280" size={16} />
            </div>
          )
        })}
        {filteredFields.length === 0 && (
          <div className="ds-px-4 ds-py-2 ds-text-[14px] ds-text-[#6b7280]">
            No matching fields found
          </div>
        )}
      </div>
    </div>
  )
}

// Field Details Component
const FieldDetails = ({
  hoveredField = null,
  selectedField = null,
  onOperatorSelect,
  fields,
}: FieldDetailsProps) => {
  const getFieldDescription = (fieldName: string | null) => {
    if (!fieldName) return ''
    const field = fields.find((f) => f.name === fieldName)
    return field?.description || `Description for ${fieldName} field.`
  }

  const getFieldOperators = (fieldName: string | null) => {
    if (!fieldName) return []
    const field = fields.find((f) => f.name === fieldName)
    return field?.operators || []
  }

  const handleOperatorClick = (field: string, operator: string) => {
    // Find the field and operator pattern
    // const fieldConfig = fields.find((f) => f.name === field);
    // const operatorConfig = fieldConfig?.operators.find((op) => op.operator === operator);
    // const pattern = operatorConfig?.pattern;

    // Check if pattern requires quotes
    // const needsQuotes = pattern?.includes('"');
    // const newValue = `${field} ${operator}${needsQuotes ? ' ""' : ""}`;

    onOperatorSelect?.(field, operator)
  }

  return (
    <div className="ds-w-2/3 ds-bg-[#fff] ds-p-12 ds-text-[#000]">
      {hoveredField && !(selectedField && selectedField === hoveredField) && (
        <div>
          <div className="ds-mb-2 ds-text-[14px] ds-text-[#6b7280]">
            {hoveredField}
          </div>
          <div className="ds-text-[14px]">
            {getFieldDescription(hoveredField)}
          </div>
        </div>
      )}

      {((selectedField && !hoveredField) ||
        (selectedField && hoveredField && selectedField === hoveredField)) && (
        <div className="ds-flex ds-flex-col ds-gap-2">
          <div className="ds-mb-2 ds-text-[14px]">
            Choose an operator for {selectedField}:
          </div>
          <div className="ds-flex ds-flex-col ds-gap-1">
            {getFieldOperators(selectedField).map(({ operator }) => (
              <div
                key={operator}
                className="ds-hover:bg-[#f3f4f6] ds-rounded ds-cursor-pointer ds-px-3 ds-py-2 ds-text-[14px]"
                onClick={() =>
                  selectedField && handleOperatorClick(selectedField, operator)
                }
              >
                {operator}
              </div>
            ))}
          </div>
        </div>
      )}

      {!hoveredField && !selectedField && (
        <div>
          <div className="ds-mb-2 ds-text-[14px] ds-text-[#6b7280]">
            Dataset Field
          </div>
          <div className="ds-text-[14px]">
            Select a field to add a filter clause.
          </div>
        </div>
      )}
    </div>
  )
}

// Filter Pills Component
const FilterPills = ({ filters, onRemove, onEdit }: FilterPillsProps) => (
  <>
    {filters?.map((filter) => (
      <Pill
        key={filter.id}
        variant={filter.variant}
        size="md"
        onClick={() => onEdit(filter.id)}
        leftIcon={
          <div className="ds-group ds-relative hover:ds-bg-[#f3f4f6]">
            <XIcon
              onClick={(e) => {
                e.stopPropagation()
                onRemove(filter.id)
              }}
              size={12}
              className="ds-cursor-pointer ds-text-[#6b7280] ds-transition-colors hover:ds-text-[#111827]"
            />
          </div>
        }
        className="ds-border-l ds-border-r ds-rounded-0 ds-border-b-0 ds-border-t-0"
      >
        {filter.label}
      </Pill>
    ))}
  </>
)

// Dropdown Content Component
const FilterDropdownContent = ({
  searchValue,
  onSearchChange,
  filteredFields,
  onFieldSelect,
  fields,
  operators,
  isOpen,
}: FilterDropdownContentProps) => {
  const [hoveredField, setHoveredField] = useState<string | null>(null)
  const [selectedField, setSelectedField] = useState<string | null>(null)
  const ref = useRef<HTMLInputElement>(null)

  const handleFieldClick = (field: string) => {
    setSelectedField(field)
  }

  const handleOperatorSelect = (field: string, operator: string) => {
    // Find the field and operator pattern
    const fieldConfig = fields.find((f) => f.name === field)
    const operatorConfig = fieldConfig?.operators.find(
      (op) => op.operator === operator,
    )
    const pattern = operatorConfig?.pattern

    // Check if pattern requires quotes
    const needsQuotes = pattern?.includes('"')
    const newValue = `${field} ${operator}${needsQuotes ? ' ""' : ''}`

    onSearchChange({
      target: { value: newValue },
    } as React.ChangeEvent<HTMLInputElement>)

    if (!needsQuotes) {
      onFieldSelect(newValue, field, operator)
      onSearchChange({
        target: { value: '' },
      } as React.ChangeEvent<HTMLInputElement>)
    }

    setSelectedField(null)
    setHoveredField(null)

    ref.current!.focus()

    // Set cursor position inside quotes if needed
    setTimeout(() => {
      if (ref.current && needsQuotes) {
        const inputElement = ref.current as HTMLInputElement
        inputElement.setSelectionRange(newValue.length - 1, newValue.length - 1)
      }
    }, 0)
  }

  useEffect(() => {
    if (!isOpen) {
      onSearchChange({
        target: { value: '' },
      } as React.ChangeEvent<HTMLInputElement>)
      setSelectedField(null)
      setHoveredField(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  return (
    <DropdownMenuContent
      style={{ zIndex: 99999, height: 'auto' }}
      sideOffset={12}
      align="start"
      className="ds-z-[99999] ds-min-w-[600px] ds-overflow-hidden ds-rounded-[16px] ds-p-0"
    >
      <div style={{ height: 'auto' }}>
        <div className="ds-px-8 ds-py-8">
          <SearchInput
            value={searchValue}
            onChange={onSearchChange}
            onSubmit={(label, selectedField, operator, value) => {
              onFieldSelect(label, selectedField, operator, value)
            }}
            fields={fields}
            operators={operators}
            forwardRef={ref}
          />
        </div>
        <div className="ds-mt-4 ds-flex ds-h-[300px] ds-w-full ds-flex-row ds-border-t-half ds-border-white-400">
          <FieldList
            filteredFields={filteredFields}
            onFieldClick={handleFieldClick}
            onFieldHover={setHoveredField}
            selectedField={selectedField}
            fields={fields}
          />
          <FieldDetails
            hoveredField={hoveredField}
            selectedField={selectedField}
            onOperatorSelect={handleOperatorSelect}
            operators={operators}
            fields={fields}
          />
        </div>
      </div>
    </DropdownMenuContent>
  )
}

export const FilterBar = ({
  fields = [],
  operators = [
    '==',
    '!=',
    'exists',
    'not-exists',
    'starts-with',
    'not-starts-with',
    'ends-with',
    'not-ends-with',
    'contains',
    'not-contains',
  ],
  onFilterChange,
  className = '',
  filters,
  setFilters,
  setCaseSensitive,
  caseSensitive,
}: FilterBarProps) => {
  const [searchValue, setSearchValue] = useState('')
  const [open, setOpen] = useState(false)
  const [editState] = useState<{
    id: string
    field: string
    operator: string
  } | null>(null)
  const editInputRef = useRef<HTMLInputElement>(null)

  const handleAddFilter = (
    label: string,
    _field: string,
    operator: string,
    value?: string,
  ) => {
    // Parse the filter value
    const fieldName = label.split(' ')[0]!.trim()
    const filterValue = value?.replace(/^"|"$/g, '')

    // Find the field and its operator configuration
    const field = fields.find((f) => f.name === fieldName)
    const operatorConfig = field?.operators.find(
      (op) => op.operator === operator,
    )
    // Only add the filter if the value matches the pattern
    if (field && operatorConfig) {
      const pattern = new RegExp(operatorConfig.pattern)
      if (pattern.test(label)) {
        const newFilter: Filter = {
          id: Math.random().toString(36).substr(2, 9),
          label: label,
          field: fieldName,
          operator: operator,
          value: filterValue,
          variant: 'default',
        }
        const newFilters = [...(filters ?? []), newFilter]
        setFilters?.(newFilters)
        onFilterChange?.(newFilters)
        setOpen(false)
      }
    }
  }

  const handleRemoveFilter = (filterId: string) => {
    const newFilters = filters
      ? filters.filter((filter) => filter.id !== filterId)
      : []
    setFilters?.(newFilters)
    onFilterChange?.(newFilters)
  }

  // Create searchable items for fuzzy search
  const searchItems = fields.flatMap((field) =>
    field.operators.map((op) => ({
      field: field.name,
      operator: op.operator,
      searchStr: `${field.name} ${op.operator}`,
    })),
  )

  // Configure Fuse for fuzzy search
  const fuse = new Fuse(searchItems, {
    keys: ['searchStr'],
    threshold: 0.3,
    distance: 100,
  })

  // Get fuzzy search results
  const filteredFields = searchValue
    ? Array.from(
        new Set(fuse.search(searchValue).map((result) => result.item.field)),
      )
    : fields.map((field) => field.name)

  const handleEditFilter = () => {
    // const filter = filters.find(f => f.id === filterId);
    // if (filter) {
    //     setEditState({ id: filterId, field: filter.label.split(' ')[0]!.trim(), operator: filter.label.split(' ')[1]!.trim() });
    //     editInputRef.current!.value = `${filter.label.split(' ')[0]!.trim()} ${filter.label.split(' ')[1]!.trim()}`;
    //     setTimeout(() => {
    //         editInputRef.current?.focus();
    //     }, 50);
    // }
  }

  return (
    <div
      className={`ds-rounded-md ds-flex ds-h-[32px] ds-w-full ds-flex-wrap ds-items-center ds-gap-2 ds-border-half ds-border-white-400 ds-bg-[#fff] ds-p-0 ds-text-[#000] ${className}`}
      style={{
        minWidth: '600px',
        width: '100%',
        color: '#000',
        borderRadius: '8px',
        flexWrap: 'wrap',
        height: 'auto',
      }}
    >
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <div
          className="ds-ml-8 ds-flex ds-flex-1 ds-items-center ds-gap-2 ds-outline-none "
          style={{ outline: 'none', height: '29px' }}
        >
          <div className="mr-[5px] text-black-500 ds-ml-3">
            <FilterIcon size={16} strokeWidth={1.5} />
          </div>
          <Input
            type="text"
            onChange={() => {}}
            ref={editInputRef}
            className="ds-rounded ds-w-[200px] ds-px-2 ds-py-1 ds-text-[14px]"
            style={{
              fontSize: '13px',
              border: 0,
              paddingLeft: '4px',
              paddingTop: '0px',
              paddingBottom: '0px',
              outline: 'none',
              display: editState ? 'block' : 'none',
              width: '100%',
            }}
          />
          <div
            style={{
              display: editState ? 'none' : 'flex',
              flexWrap: 'wrap',
              gap: '6px',
              height: '29px',
            }}
          >
            <FilterPills
              filters={filters ?? []}
              onRemove={handleRemoveFilter}
              onEdit={handleEditFilter}
            />
          </div>
          <DropdownMenuTrigger style={{ outline: 'none' }}>
            {!editState && (
              <div
                className="flex-1 ds-ml-4 ds-mt-2 ds-flex ds-items-center ds-gap-2"
                onClick={() => setOpen(true)}
              >
                {filters && filters.length > 0 ? (
                  <Button variant="secondary_outline" size="24">
                    <PlusIcon color="#6b7280" size={16} />
                  </Button>
                ) : (
                  <div className="ds-select-none ds-text-13 ds-text-black-700">
                    Click to add filter
                  </div>
                )}
              </div>
            )}
          </DropdownMenuTrigger>
        </div>
        <FilterDropdownContent
          searchValue={searchValue}
          onSearchChange={(e) => setSearchValue(e.target.value)}
          filteredFields={filteredFields}
          onFieldSelect={handleAddFilter}
          fields={fields}
          operators={operators}
          isOpen={open}
          onClose={() => setOpen(false)}
        />
      </DropdownMenu>

      <div
        className="ds-border-l ds-ml-8 ds-ml-auto ds-flex ds-items-center ds-gap-2 ds-border-l-[#e5e7eb] ds-p-2"
        style={{
          paddingLeft: '4px',
          paddingRight: '2px',
          borderLeft: '1px solid #e5e7eb',
        }}
        title={
          caseSensitive
            ? 'Disable case sensitive search'
            : 'Enable case sensitive search'
        }
      >
        <Button
          onClick={() => setCaseSensitive(!caseSensitive)}
          variant="secondary_outline"
          size="24"
          style={{
            padding: '4px 2px',
            background: caseSensitive ? '#f3f4f6' : 'transparent',
          }}
          title={
            caseSensitive
              ? 'Disable case sensitive search'
              : 'Enable case sensitive search'
          }
        >
          <CaseSensitiveIcon
            size={18}
            color={caseSensitive ? '#1f2937' : '#6b7280'}
          />
        </Button>
      </div>
    </div>
  )
}
